import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';

interface Opt { id: number; name_ar: string; }

const GENDERS = [{ v: '', l: '—' }, { v: 'male', l: 'ذكر' }, { v: 'female', l: 'أنثى' }, { v: 'mixed', l: 'مختلط' }];
const PRICE_TYPES = [{ v: 'fixed', l: 'سعر ثابت' }, { v: 'negotiable', l: 'قابل للتفاوض' }, { v: 'auction', l: 'مزاد' }];
const STATUSES = [{ v: 'active', l: 'نشط' }, { v: 'paused', l: 'متوقّف مؤقتاً' }, { v: 'sold', l: 'تم البيع' }];

const EditAdPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [categories, setCategories] = useState<Opt[]>([]);
  const [subcategories, setSubcategories] = useState<Opt[]>([]);
  const [regions, setRegions] = useState<Opt[]>([]);
  const [cities, setCities] = useState<Opt[]>([]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');
  const [regionId, setRegionId] = useState('');
  const [cityId, setCityId] = useState('');
  const [currentCity, setCurrentCity] = useState('');
  const [price, setPrice] = useState('');
  const [priceType, setPriceType] = useState('fixed');
  const [quantity, setQuantity] = useState('');
  const [weight, setWeight] = useState('');
  const [ageMonths, setAgeMonths] = useState('');
  const [gender, setGender] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [showPhone, setShowPhone] = useState(true);
  const [deliveryAvailable, setDeliveryAvailable] = useState(false);
  const [slaughterAvailable, setSlaughterAvailable] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState('');
  const [slaughterFee, setSlaughterFee] = useState('');
  const [status, setStatus] = useState('active');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [existingVideo, setExistingVideo] = useState(false);
  const [removeVideo, setRemoveVideo] = useState(false);
  const [videoCfg, setVideoCfg] = useState<{ enabled: boolean; maxDur: number; maxMb: number }>({ enabled: false, maxDur: 30, maxMb: 20 });

  // جلب الإعلان + القوائم
  useEffect(() => {
    const load = async () => {
      try {
        const [adRes, catRes, regRes, cfgRes] = await Promise.all([
          api.get('/ads/' + id),
          api.get('/categories'),
          api.get('/regions'),
          api.get('/app-config').catch(() => null),
        ]);
        setCategories(catRes.data.data || []);
        setRegions(regRes.data.data || []);
        const va = (cfgRes as any)?.data?.data?.ads;
        if (va) setVideoCfg({ enabled: !!va.video_enabled, maxDur: Number(va.video_max_duration_sec ?? 30), maxMb: Number(va.video_max_size_mb ?? 20) });
        const a = adRes.data.data;
        if (!a) { setNotFound(true); return; }
        setTitle(a.title || '');
        setDescription(a.description || '');
        setCategoryId(a.category?.id ? String(a.category.id) : '');
        setSubcategoryId(a.subcategory?.id ? String(a.subcategory.id) : '');
        setCityId(a.city?.id ? String(a.city.id) : '');
        setCurrentCity(a.city?.name_ar || '');
        setPrice(a.price != null ? String(a.price) : '');
        setPriceType(a.price_type || 'fixed');
        setQuantity(a.quantity != null ? String(a.quantity) : '');
        setWeight(a.weight != null ? String(a.weight) : '');
        setAgeMonths(a.age_months != null ? String(a.age_months) : '');
        setGender(a.gender || '');
        setContactPhone(a.contact_phone || '');
        setShowPhone(a.show_phone !== false);
        setDeliveryAvailable(!!a.delivery_available);
        setSlaughterAvailable(!!a.slaughter_available);
        setDeliveryFee(a.delivery_fee != null ? String(a.delivery_fee) : '');
        setSlaughterFee(a.slaughter_fee != null ? String(a.slaughter_fee) : '');
        setStatus(a.status && ['active', 'paused', 'sold'].includes(a.status) ? a.status : 'active');
        setExistingVideo(!!a.has_video);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    if (categoryId) {
      api.get('/categories/' + categoryId + '/subcategories').then(r => setSubcategories(r.data.data || [])).catch(() => setSubcategories([]));
    } else { setSubcategories([]); }
  }, [categoryId]);

  useEffect(() => {
    if (regionId) {
      api.get('/regions/' + regionId + '/cities').then(r => setCities(r.data.data || [])).catch(() => setCities([]));
    } else { setCities([]); }
  }, [regionId]);

  const save = useCallback(async () => {
    setError('');
    if (!title.trim() || title.trim().length < 5) { setError('العنوان يجب أن يكون 5 أحرف على الأقل'); return; }
    if (!description.trim() || description.trim().length < 10) { setError('الوصف يجب أن يكون 10 أحرف على الأقل'); return; }
    if (!price || Number(price) < 0) { setError('أدخل سعراً صحيحاً'); return; }
    try {
      setSaving(true);
      const body: any = {
        title: title.trim(),
        description: description.trim(),
        price: Number(price),
        price_type: priceType,
        show_phone: showPhone,
        delivery_available: deliveryAvailable,
        slaughter_available: slaughterAvailable,
        delivery_fee: deliveryAvailable && deliveryFee !== '' ? Number(deliveryFee) : null,
        slaughter_fee: slaughterAvailable && slaughterFee !== '' ? Number(slaughterFee) : null,
        status,
      };
      if (categoryId) body.category_id = Number(categoryId);
      if (subcategoryId) body.subcategory_id = Number(subcategoryId);
      if (cityId) body.city_id = Number(cityId);
      if (quantity) body.quantity = Number(quantity);
      if (weight) body.weight = Number(weight);
      if (ageMonths) body.age_months = Number(ageMonths);
      if (gender) body.gender = gender;
      if (contactPhone) body.contact_phone = contactPhone;

      await api.put('/ads/' + id, body);
      if (videoFile) {
        const vf = new FormData();
        vf.append('video', videoFile);
        try { await api.post('/ads/' + id + '/video', vf, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch {}
      } else if (removeVideo) {
        try { await api.delete('/ads/' + id + '/video'); } catch {}
      }
      navigate('/ads/' + id);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'تعذّر حفظ التعديلات، تحقّق من الحقول';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }, [id, title, description, price, priceType, showPhone, deliveryAvailable, slaughterAvailable, deliveryFee, slaughterFee, status, categoryId, subcategoryId, cityId, quantity, weight, ageMonths, gender, contactPhone, videoFile, removeVideo, navigate]);

  if (loading) return <div className="ea-wrap"><div className="ea-card" style={{ padding: 40, textAlign: 'center', color: 'var(--aj-muted)' }}>جاري التحميل...</div></div>;
  if (notFound) return <div className="ea-wrap"><div className="ea-card" style={{ padding: 40, textAlign: 'center' }}><p style={{ color: 'var(--aj-muted)' }}>الإعلان غير موجود أو لا تملك صلاحية تعديله.</p><Link to="/my/ads" style={{ color: 'var(--aj-primary)', fontWeight: 700 }}>← إعلاناتي</Link></div></div>;

  return (
    <>
      <style>{css}</style>
      <div className="ea-wrap">
        <div className="ea-head">
          <h1>تعديل الإعلان</h1>
          <Link to={'/ads/' + id} className="ea-cancel">إلغاء</Link>
        </div>

        <div className="ea-card">
          {error && <div className="ea-error">{error}</div>}

          <div className="ea-field">
            <label>العنوان *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} maxLength={200} placeholder="عنوان الإعلان" />
          </div>

          <div className="ea-field">
            <label>الوصف *</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} maxLength={5000} rows={5} placeholder="تفاصيل الإعلان..." />
          </div>

          {videoCfg.enabled && (
            <div className="ea-field">
              <label>فيديو الإعلان (اختياري) — حتى {videoCfg.maxDur}ث · {videoCfg.maxMb}MB</label>
              {existingVideo && !videoFile && !removeVideo ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: 'var(--aj-muted)' }}>📹 يوجد فيديو مرفوع</span>
                  <button type="button" onClick={() => setRemoveVideo(true)} style={{ color: '#F43F5E', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>حذف</button>
                </div>
              ) : (
                <input
                  type="file"
                  accept="video/mp4,video/quicktime"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    if (f && f.size > videoCfg.maxMb * 1024 * 1024) { setError('حجم الفيديو يتجاوز ' + videoCfg.maxMb + 'MB'); return; }
                    setVideoFile(f); setRemoveVideo(false);
                  }}
                />
              )}
              {videoFile && <p style={{ fontSize: 12, color: 'var(--aj-muted)', marginTop: 4 }}>📹 {videoFile.name}</p>}
            </div>
          )}

          <div className="ea-grid">
            <div className="ea-field">
              <label>التصنيف</label>
              <select value={categoryId} onChange={e => { setCategoryId(e.target.value); setSubcategoryId(''); }}>
                <option value="">اختر التصنيف</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name_ar}</option>)}
              </select>
            </div>
            {subcategories.length > 0 && (
              <div className="ea-field">
                <label>السلالة</label>
                <select value={subcategoryId} onChange={e => setSubcategoryId(e.target.value)}>
                  <option value="">اختر السلالة</option>
                  {subcategories.map(sc => <option key={sc.id} value={sc.id}>{sc.name_ar}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="ea-grid">
            <div className="ea-field">
              <label>السعر (ر.س) *</label>
              <input type="number" min="0" value={price} onChange={e => setPrice(e.target.value)} />
            </div>
            <div className="ea-field">
              <label>نوع السعر</label>
              <select value={priceType} onChange={e => setPriceType(e.target.value)}>
                {PRICE_TYPES.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
            </div>
          </div>

          <div className="ea-grid">
            <div className="ea-field">
              <label>الكمية (رأس)</label>
              <input type="number" min="1" value={quantity} onChange={e => setQuantity(e.target.value)} />
            </div>
            <div className="ea-field">
              <label>الوزن (كجم)</label>
              <input type="number" min="0" value={weight} onChange={e => setWeight(e.target.value)} />
            </div>
            <div className="ea-field">
              <label>العمر (شهر)</label>
              <input type="number" min="0" value={ageMonths} onChange={e => setAgeMonths(e.target.value)} />
            </div>
            <div className="ea-field">
              <label>الجنس</label>
              <select value={gender} onChange={e => setGender(e.target.value)}>
                {GENDERS.map(g => <option key={g.v} value={g.v}>{g.l}</option>)}
              </select>
            </div>
          </div>

          <div className="ea-field">
            <label>الموقع {currentCity && <span style={{ color: 'var(--aj-muted)', fontWeight: 400 }}>(الحالي: {currentCity})</span>}</label>
            <div className="ea-grid2">
              <select value={regionId} onChange={e => { setRegionId(e.target.value); setCityId(''); }}>
                <option value="">لتغيير المدينة: اختر المنطقة</option>
                {regions.map(r => <option key={r.id} value={r.id}>{r.name_ar}</option>)}
              </select>
              <select value={cityId} onChange={e => setCityId(e.target.value)} disabled={!regionId}>
                <option value="">{regionId ? 'اختر المدينة' : 'أبقِ الحالية'}</option>
                {cities.map(c => <option key={c.id} value={c.id}>{c.name_ar}</option>)}
              </select>
            </div>
          </div>

          <div className="ea-grid">
            <div className="ea-field">
              <label>رقم التواصل</label>
              <input value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="05XXXXXXXX" dir="ltr" />
            </div>
            <div className="ea-field">
              <label>حالة الإعلان</label>
              <select value={status} onChange={e => setStatus(e.target.value)}>
                {STATUSES.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>
          </div>

          <div className="ea-toggles">
            <label><input type="checkbox" checked={showPhone} onChange={e => setShowPhone(e.target.checked)} /> إظهار رقم الجوال في الإعلان</label>
            <label><input type="checkbox" checked={deliveryAvailable} onChange={e => setDeliveryAvailable(e.target.checked)} /> التوصيل متاح</label>
            {deliveryAvailable && (
              <div style={{ paddingRight: 24 }}>
                <span style={{ fontSize: 13, color: '#4B5563', display: 'block', marginBottom: 6 }}>رسوم التوصيل</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button type="button" onClick={() => setDeliveryFee('')} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid ' + (deliveryFee === '' ? '#005343' : '#E5E7EB'), background: deliveryFee === '' ? '#005343' : '#fff', color: deliveryFee === '' ? '#fff' : '#4B5563', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>مجاني</button>
                  <button type="button" onClick={() => setDeliveryFee(deliveryFee || '0')} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid ' + (deliveryFee !== '' ? '#005343' : '#E5E7EB'), background: deliveryFee !== '' ? '#005343' : '#fff', color: deliveryFee !== '' ? '#fff' : '#4B5563', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>برسوم</button>
                  {deliveryFee !== '' && <input type="number" min="0" value={deliveryFee} onChange={e => setDeliveryFee(e.target.value)} placeholder="ر.س" style={{ width: 110, border: '1px solid #E5E7EB', borderRadius: 8, padding: '7px 10px', fontSize: 13 }} />}
                </div>
              </div>
            )}
            <label><input type="checkbox" checked={slaughterAvailable} onChange={e => setSlaughterAvailable(e.target.checked)} /> الذبح متاح</label>
            {slaughterAvailable && (
              <div style={{ paddingRight: 24 }}>
                <span style={{ fontSize: 13, color: '#4B5563', display: 'block', marginBottom: 6 }}>رسوم الذبح</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button type="button" onClick={() => setSlaughterFee('')} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid ' + (slaughterFee === '' ? '#005343' : '#E5E7EB'), background: slaughterFee === '' ? '#005343' : '#fff', color: slaughterFee === '' ? '#fff' : '#4B5563', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>مجاني</button>
                  <button type="button" onClick={() => setSlaughterFee(slaughterFee || '0')} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid ' + (slaughterFee !== '' ? '#005343' : '#E5E7EB'), background: slaughterFee !== '' ? '#005343' : '#fff', color: slaughterFee !== '' ? '#fff' : '#4B5563', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>برسوم</button>
                  {slaughterFee !== '' && <input type="number" min="0" value={slaughterFee} onChange={e => setSlaughterFee(e.target.value)} placeholder="ر.س" style={{ width: 110, border: '1px solid #E5E7EB', borderRadius: 8, padding: '7px 10px', fontSize: 13 }} />}
                </div>
              </div>
            )}
          </div>

          <div className="ea-actions">
            <button className="ea-save" onClick={save} disabled={saving}>{saving ? 'جاري الحفظ...' : '💾 حفظ التعديلات'}</button>
            <Link to={'/ads/' + id} className="ea-cancel2">إلغاء</Link>
          </div>
          <p className="ea-note">ملاحظة: تعديل صور الإعلان يتم من صفحة الإعلان لاحقاً.</p>
        </div>
      </div>
    </>
  );
};

const css = `
.ea-wrap{max-width:760px;margin:24px auto;padding:0 16px;font-family:var(--aj-font)}
.ea-wrap *{box-sizing:border-box}
.ea-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:16px}
.ea-head h1{font-size:22px;font-weight:800;color:var(--aj-primary-dark);margin:0}
.ea-cancel{color:var(--aj-muted);font-size:13px;text-decoration:none;font-weight:600}
.ea-card{background:#fff;border:1px solid var(--aj-border);border-radius:var(--aj-radius);padding:24px;display:flex;flex-direction:column;gap:16px;box-shadow:var(--aj-shadow)}
.ea-error{background:#FEF2F2;border:1px solid #FECACA;color:#B91C1C;padding:10px 14px;border-radius:var(--aj-radius-sm);font-size:13px;font-weight:600}
.ea-field{display:flex;flex-direction:column;gap:6px}
.ea-field label{font-size:13px;font-weight:700;color:var(--aj-text-2)}
.ea-field input,.ea-field select,.ea-field textarea{width:100%;padding:0 12px;height:44px;border:1.5px solid var(--aj-border);border-radius:var(--aj-radius-sm);font-family:var(--aj-font);font-size:14px;outline:none;background:#fff;color:var(--aj-text)}
.ea-field textarea{height:auto;padding:10px 12px;resize:vertical}
.ea-field input:focus,.ea-field select:focus,.ea-field textarea:focus{border-color:var(--aj-primary);box-shadow:0 0 0 3px rgba(17,94,84,.08)}
.ea-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.ea-grid2{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.ea-toggles{display:flex;flex-direction:column;gap:10px;padding:4px 0}
.ea-toggles label{display:flex;align-items:center;gap:8px;font-size:14px;color:var(--aj-text-2);cursor:pointer}
.ea-toggles input{width:18px;height:18px;accent-color:var(--aj-primary)}
.ea-actions{display:flex;gap:12px;align-items:center;margin-top:6px}
.ea-save{background:var(--aj-primary);color:#fff;border:none;border-radius:var(--aj-radius-sm);height:46px;padding:0 26px;font-family:var(--aj-font);font-size:15px;font-weight:700;cursor:pointer}
.ea-save:hover{background:var(--aj-primary-dark)}
.ea-save:disabled{opacity:.6;cursor:not-allowed}
.ea-cancel2{color:var(--aj-text-2);font-size:14px;text-decoration:none;font-weight:600}
.ea-note{font-size:12px;color:var(--aj-muted);margin:4px 0 0}
@media(max-width:600px){.ea-grid{grid-template-columns:1fr}}
`;

export default EditAdPage;
